import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LandingLayout from "./layouts/LandingLayout";
import AppLayout from "./layouts/AppLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import ProviderRoute from "./components/ProviderRoute";
import RedirectIfAuth from "./components/RedirectIfAuth";
import TestModeBanner from "./components/TestModeBanner";
import CookieBanner from "./components/CookieBanner";
import Footer from "./components/Footer";
import { FooterOrganizerProvider } from "./hooks/useFooterOrganizer";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import packageJson from "../package.json";
import "./App.css";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const Settings = lazy(() => import("./pages/Settings"));
const PublicEventPage = lazy(() => import("./pages/PublicEventPage"));
const TicketList = lazy(() => import("./pages/TicketList"));
const TicketDetail = lazy(() => import("./pages/TicketDetail"));
const Validate = lazy(() => import("./pages/Validate"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const Cookies = lazy(() => import("./pages/Cookies"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Recover = lazy(() => import("./pages/Recover"));
const EventBranding = lazy(() => import("./pages/EventBranding"));
const EventOverview = lazy(() => import("./pages/EventOverview"));
const Sales = lazy(() => import("./pages/Sales"));
const RgpdRightsForm = lazy(() => import("./pages/RgpdRightsForm"));
const Notify = lazy(() => import("./pages/Notify"));
const CancelEvent = lazy(() => import("./pages/CancelEvent"));
const McpIntegrations = lazy(() => import("./pages/McpIntegrations"));
const Mcp = lazy(() => import("./pages/Mcp"));
const CreateOrganization = lazy(() => import("./pages/CreateOrganization"));
const OrgSettings = lazy(() => import("./pages/OrgSettings"));
const OrgMembers = lazy(() => import("./pages/OrgMembers"));
const OrgBranding = lazy(() => import("./pages/OrgBranding"));
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const DoorIndex = lazy(() => import("./pages/Door/DoorIndex"));
const DoorScanner = lazy(() => import("./pages/Door/DoorScanner"));
const EmbedEventPage = lazy(() => import("./pages/EmbedEventPage"));
const EventEmbedSnippet = lazy(() => import("./pages/EventEmbedSnippet"));
const ProviderDocs = lazy(() => import("./pages/ProviderDocs/ProviderDocs"));

function RouteFallback() {
  return <div className="route-fallback" aria-busy="true" />;
}

export default function App() {
  return (
    <FooterOrganizerProvider>
      <TestModeBanner />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<LandingLayout />}>
            <Route
              path="/"
              element={
                <RedirectIfAuth>
                  <Landing />
                </RedirectIfAuth>
              }
            />
          </Route>

          <Route
            path="/login"
            element={
              <RedirectIfAuth>
                <Login />
              </RedirectIfAuth>
            }
          />
          <Route
            path="/register"
            element={
              <RedirectIfAuth>
                <Login />
              </RedirectIfAuth>
            }
          />

          <Route element={<AppLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create"
              element={
                <ProtectedRoute>
                  <CreateEvent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/:eventSlug"
              element={
                <ProtectedRoute>
                  <EventOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/:eventSlug/branding"
              element={
                <ProtectedRoute>
                  <EventBranding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/:eventSlug/sales"
              element={
                <ProtectedRoute>
                  <Sales />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/:eventSlug/notify"
              element={
                <ProtectedRoute>
                  <Notify />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/:eventSlug/cancel"
              element={
                <ProtectedRoute>
                  <CancelEvent />
                </ProtectedRoute>
              }
            />
            <Route path="/event/:id" element={<PublicEventPage />} />
            <Route
              path="/:orgSlug/:eventSlug"
              element={<PublicEventPage />}
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute>
                  <TicketList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ticket/:ticketId"
              element={
                <ProtectedRoute>
                  <TicketDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/validate"
              element={
                <ProtectedRoute>
                  <Validate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/validate/:token"
              element={
                <ProtectedRoute>
                  <Validate />
                </ProtectedRoute>
              }
            />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route
              path="/org/:slug/integrations/mcp"
              element={
                <ProtectedRoute>
                  <McpIntegrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-org"
              element={
                <ProtectedRoute>
                  <CreateOrganization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org/:slug/settings"
              element={
                <ProtectedRoute>
                  <OrgSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org/:slug/members"
              element={
                <ProtectedRoute>
                  <OrgMembers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org/:slug/branding"
              element={
                <ProtectedRoute>
                  <OrgBranding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider/dashboard"
              element={
                <ProtectedRoute>
                  <ProviderDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org/:slug/events/:eventId/embed"
              element={
                <ProtectedRoute>
                  <EventEmbedSnippet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider/docs"
              element={
                <ProviderRoute>
                  <ProviderDocs />
                </ProviderRoute>
              }
            />
            <Route
              path="/provider/docs/:section"
              element={
                <ProviderRoute>
                  <ProviderDocs />
                </ProviderRoute>
              }
            />
            <Route
              path="/provider/docs/:section/:slug"
              element={
                <ProviderRoute>
                  <ProviderDocs />
                </ProviderRoute>
              }
            />
          </Route>

          <Route path="/legal/cookies" element={<Cookies />} />
          <Route
            path="/legal/aviso-legal"
            element={<LegalPage slug="aviso-legal" title="Aviso Legal" />}
          />
          <Route
            path="/legal/privacidad"
            element={
              <LegalPage slug="privacidad" title="Política de Privacidad" />
            }
          />
          <Route
            path="/legal/terminos"
            element={
              <LegalPage slug="terminos" title="Términos y Condiciones" />
            }
          />
          <Route
            path="/legal/contrato-encargado"
            element={
              <LegalPage
                slug="contrato-encargado"
                title="Contrato de Encargado de Tratamiento"
                downloadable
              />
            }
          />
          <Route path="/legal/rgpd" element={<RgpdRightsForm />} />
          <Route
            path="/legal/accessibility"
            element={
              <LegalPage
                slug="accesibilidad"
                title="Accesibilidad"
              />
            }
          />
          <Route
            path="/legal/accesibilidad"
            element={
              <LegalPage
                slug="accesibilidad"
                title="Accesibilidad"
              />
            }
          />

          <Route path="/recover/:token" element={<Recover />} />
          {/* Forever-link alias used in purchase emails (issue #199).
              Same component, shorter URL — backwards compatible with the
              older /recover/:token shape. */}
          <Route path="/t/:token" element={<Recover />} />

          <Route
            path="/door"
            element={
              <ProtectedRoute>
                <DoorIndex />
              </ProtectedRoute>
            }
          />
          <Route
            path="/door/event/:eventId"
            element={
              <ProtectedRoute>
                <DoorScanner />
              </ProtectedRoute>
            }
          />

          <Route path="/mcp" element={<Mcp />} />

          {/* Embeddable checkout widget (issue #195). These routes
              render outside of any Pikra layout so they can be safely
              iframed from third-party organiser sites. */}
          <Route path="/embed/event/:id" element={<EmbedEventPage />} />
          <Route
            path="/embed/:orgSlug/:eventSlug"
            element={<EmbedEventPage />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Footer />
      <CookieBanner />
      <span className="app-version">v{packageJson.version}</span>
    </FooterOrganizerProvider>
  );
}
